import { useEffect } from 'react';

import { useTaskProcessor } from '@eluve/appointment-tasks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';

import { useAppointmentDialogs } from './AppointmentDialogProvider';

export const OverwriteExistingSummaryDialog: React.FC<{
  isOpen: boolean;
  appointmentId?: string;
}> = ({ isOpen, appointmentId }) => {
  const analytics = useAnalytics();
  const logger = useNamedLogger('OverwriteExistingSummaryDialog');
  const { closeOverwriteSummaryDialog } = useAppointmentDialogs();
  const { taskProcessor } = useTaskProcessor();

  const handleOutsideClick = () => {
    analytics.capture('overwriting_existing_summary_dialog_cancelled', {
      appointmentId,
    });
    logger.info('User clicked outside in OverwritingExistingSummaryDialog');
    closeOverwriteSummaryDialog();
  };

  const handleCancel = () => {
    analytics.capture('overwriting_existing_summary_dialog_cancelled', {
      appointmentId,
    });
    logger.info('User clicked cancel in OverwritingExistingSummaryDialog');
    closeOverwriteSummaryDialog();
  };

  const handleProceed = () => {
    if (!appointmentId) {
      return;
    }
    analytics.capture('overwriting_existing_summary_dialog_proceeded', {
      appointmentId,
    });
    logger.info('User clicked YES in OverwritingExistingSummaryDialog');
    taskProcessor.addTask({
      type: 'TASK.GENERATE_NOTE',
      appointmentId,
      userCompletedAt: new Date().toISOString(),
      shouldOverrideExistingSummary: true,
    });
    closeOverwriteSummaryDialog();
  };

  useEffect(() => {
    if (isOpen) {
      analytics.capture('overwriting_existing_summary_dialog_shown');
      logger.info('OverwritingExistingSummaryDialog shown');
    }
  }, [isOpen, analytics, logger]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleOutsideClick()}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="text-bg-strong-primary">
            Overwrite existing note?
          </DialogTitle>
          <DialogDescription>
            If you generate a new note your existing note will be overwritten.
            Is that ok?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            No
          </Button>
          <Button size="sm" onClick={handleProceed} disabled={!appointmentId}>
            Yes, Proceed to New Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
